import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef
} from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useRouter } from 'next/router'

import { pageHasHero } from '@/utils/page'

import { fadeAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPPageData } from '@/types'

export const TransitionContext = createContext<undefined>(undefined)

const TransitionProvider = ({
  pageProps,
  children
}: PropsWithChildren<{ pageProps: WPPageData }>) => {
  const router = useRouter()
  const active = useRef(false)
  const nodeRef = useRef<HTMLElement>(null!)
  const contextValue = useMemo(() => undefined, [])
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])

  const handleEnterTransition = () => {
    document.documentElement.classList.remove('page-transition')
    gsap.set(nodeRef.current, { opacity: 0 })
    active.current = true
  }

  const handleExitTransition = () => {
    document.documentElement.classList.add('page-transition')
    active.current = false
  }

  const transitionListeners = (done: () => void) => {
    fadeAnimation(nodeRef.current, active.current, done)
  }

  useEffect(() => {
    ScrollTrigger.clearScrollMemory('manual')

    router.beforePopState((state) => {
      state.options.scroll = false
      return true
    })
  }, [router])

  return (
    <TransitionContext.Provider value={contextValue}>
      <SwitchTransition mode="out-in">
        <Transition
          key={router.asPath}
          nodeRef={nodeRef}
          onEnter={handleEnterTransition}
          onExit={handleExitTransition}
          addEndListener={transitionListeners}
          unmountOnExit
        >
          <main
            ref={nodeRef}
            className={cn('static', {
              'pt-[var(--header-height)]': !hasHero
            })}
          >
            {children}
          </main>
        </Transition>
      </SwitchTransition>
    </TransitionContext.Provider>
  )
}

export default TransitionProvider
